import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useUI } from '@/stores/ui'
import { subscriptionsApi } from '@/api/endpoints/subscriptions'
import { SUBSCRIPTION_CONFIG } from '@/config/subscription'
import Spinner from '@/components/common/Spinner'

interface Props {
  open: boolean
  onClose: () => void
  subscriptionStatus: {
    status: string
    current_period_end?: string | null
    days_until_expiry?: number | null
  } | null
  onStatusUpdate?: () => void
}

export default function SubscriptionManagementModal({
  open,
  onClose,
  subscriptionStatus,
  onStatusUpdate,
}: Props) {
  const addToast = useUI((state) => state.addToast)
  const [action, setAction] = useState<'idle' | 'cancel' | 'renew' | 'billing'>('idle')
  const [voucherCode, setVoucherCode] = useState('')
  const [validatingVoucher, setValidatingVoucher] = useState(false)
  const [voucherValid, setVoucherValid] = useState<boolean | null>(null)
  const [voucherInfo, setVoucherInfo] = useState<{ type?: string; percentage?: number } | null>(null)
  const [processing, setProcessing] = useState(false)
  const [cancelConfirm, setCancelConfirm] = useState('')

  const handleValidateVoucher = async () => {
    if (!voucherCode.trim()) {
      setVoucherValid(false)
      return
    }
    setValidatingVoucher(true)
    try {
      const result = await subscriptionsApi.validateVoucher(voucherCode.trim())
      setVoucherValid(result.valid)
      if (result.valid) {
        setVoucherInfo({
          type: result.discount_type || undefined,
          percentage: result.discount_percentage || undefined,
        })
      } else {
        setVoucherInfo(null)
      }
    } catch (error: any) {
      addToast({
        message: error.response?.data?.detail || 'Failed to validate voucher',
        type: 'error',
      })
      setVoucherValid(false)
      setVoucherInfo(null)
    } finally {
      setValidatingVoucher(false)
    }
  }

  const handleCancel = async () => {
    if (cancelConfirm !== 'cancel') {
      addToast({ message: 'Please type "cancel" to confirm', type: 'error' })
      return
    }

    if (!confirm('Are you sure you want to cancel your subscription? You will continue to have access until the end of your current billing period.')) {
      return
    }

    setProcessing(true)
    try {
      await subscriptionsApi.cancel()
      addToast({ message: 'Subscription cancelled successfully', type: 'success' })
      setAction('idle')
      setCancelConfirm('')
      onStatusUpdate?.()
      onClose()
    } catch (error: any) {
      addToast({
        message: error.response?.data?.detail || 'Failed to cancel subscription',
        type: 'error',
      })
    } finally {
      setProcessing(false)
    }
  }

  const handleRenew = async () => {
    setProcessing(true)
    try {
      const result = await subscriptionsApi.renew(voucherCode.trim() || undefined)
      // Redirect to Stripe checkout
      window.location.href = result.url
    } catch (error: any) {
      addToast({
        message: error.response?.data?.detail || 'Failed to create renewal checkout',
        type: 'error',
      })
      setProcessing(false)
    }
  }

  const handleBillingPortal = async () => {
    setProcessing(true)
    try {
      const result = await subscriptionsApi.createBillingPortal()
      // Redirect to Stripe billing portal
      window.location.href = result.url
    } catch (error: any) {
      addToast({
        message: error.response?.data?.detail || 'Failed to open billing portal',
        type: 'error',
      })
      setProcessing(false)
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6"
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Manage Subscription</h3>
              <button
                className="text-gray-400 hover:text-gray-600 focus-ring rounded"
                onClick={onClose}
                disabled={processing}
              >
                ✕
              </button>
            </div>

            {action === 'idle' && (
              <div className="space-y-4">
                {/* Subscription Status */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-sm font-medium text-gray-700 mb-2">Current Status</div>
                  <div className="text-lg font-semibold text-green-600 capitalize mb-2">
                    {subscriptionStatus?.status || 'Active'}
                  </div>
                  {subscriptionStatus?.current_period_end && (
                    <div className="text-sm text-gray-600">
                      Next billing cycle: {new Date(subscriptionStatus.current_period_end).toLocaleDateString()}
                      {subscriptionStatus.days_until_expiry !== null && subscriptionStatus.days_until_expiry !== undefined && (
                        <span className="ml-2">
                          ({subscriptionStatus.days_until_expiry} days remaining)
                        </span>
                      )}
                    </div>
                  )}
                  <div className="text-sm text-gray-600 mt-2">
                    Annual subscription: {SUBSCRIPTION_CONFIG.ANNUAL_PRICE_FORMATTED}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    className="w-full btn-primary text-left p-4 flex items-center justify-between"
                    onClick={() => setAction('renew')}
                    disabled={processing}
                  >
                    <div>
                      <div className="font-medium">Renew Subscription</div>
                      <div className="text-sm text-gray-600">Renew for the next year (voucher codes supported)</div>
                    </div>
                    <span>→</span>
                  </button>

                  <button
                    className="w-full btn-secondary text-left p-4 flex items-center justify-between"
                    onClick={() => setAction('billing')}
                    disabled={processing}
                  >
                    <div>
                      <div className="font-medium">Autopay Settings & ACH Details</div>
                      <div className="text-sm text-gray-600">Update payment methods and manage autopay</div>
                    </div>
                    <span>→</span>
                  </button>

                  <button
                    className="w-full btn-secondary text-left p-4 flex items-center justify-between border-red-200 text-red-700 hover:bg-red-50"
                    onClick={() => setAction('cancel')}
                    disabled={processing}
                  >
                    <div>
                      <div className="font-medium">Cancel Subscription</div>
                      <div className="text-sm text-gray-600">Cancel your subscription (access continues until period end)</div>
                    </div>
                    <span>→</span>
                  </button>
                </div>

                {/* Financial Assistance Note */}
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Struggling to make ends meet at your parish?</strong> We don't want to turn anyone away because of finances. Let us know, and we'll see what we can work out!
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <button
                    className="btn-secondary"
                    onClick={onClose}
                    disabled={processing}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

            {action === 'cancel' && (
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-semibold mb-2">Cancel Subscription</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Your subscription will be cancelled, but you'll continue to have access until{' '}
                    {subscriptionStatus?.current_period_end
                      ? new Date(subscriptionStatus.current_period_end).toLocaleDateString()
                      : 'the end of your billing period'}.
                  </p>
                  <p className="text-sm text-gray-600 mb-4">
                    <strong>Note:</strong> Cancellations are non-refundable after the 30-day refund period.
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-3">
                    <strong>Note:</strong> You can utilize previous-year's data in future years to jump-start class calendar creation - so we wouldn't recommend deleting if you plan to use this software in the future.
                  </p>
                  <label className="block text-sm font-medium mb-1">
                    Type "cancel" to confirm:
                  </label>
                  <input
                    type="text"
                    className="input-field w-full focus-ring"
                    value={cancelConfirm}
                    onChange={(e) => setCancelConfirm(e.target.value)}
                    placeholder="Type 'cancel' to confirm"
                    disabled={processing}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      setAction('idle')
                      setCancelConfirm('')
                    }}
                    disabled={processing}
                  >
                    Back
                  </button>
                  <button
                    className="btn-secondary bg-red-600 text-white hover:bg-red-700"
                    onClick={handleCancel}
                    disabled={processing || cancelConfirm !== 'cancel'}
                  >
                    {processing ? <Spinner label="Cancelling..." /> : 'Cancel Subscription'}
                  </button>
                </div>
              </div>
            )}

            {action === 'renew' && (
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-semibold mb-2">Renew Subscription</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Renew your subscription for the next year. You can apply a voucher code if you have one.
                  </p>
                  <p className="text-sm text-gray-600 mb-4">
                    <strong>Note:</strong> Renewals are non-refundable.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Voucher Code (Optional)</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="input-field flex-1 focus-ring"
                      value={voucherCode}
                      onChange={(e) => {
                        setVoucherCode(e.target.value)
                        setVoucherValid(null)
                        setVoucherInfo(null)
                      }}
                      placeholder="Enter voucher code"
                      disabled={processing || validatingVoucher}
                    />
                    <button
                      className="btn-secondary whitespace-nowrap"
                      onClick={handleValidateVoucher}
                      disabled={processing || validatingVoucher || !voucherCode.trim()}
                    >
                      {validatingVoucher ? 'Validating...' : 'Validate'}
                    </button>
                  </div>
                  {voucherValid === true && voucherInfo && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                      ✓ Voucher valid! {voucherInfo.type === 'percentage' && voucherInfo.percentage
                        ? `${voucherInfo.percentage}% discount`
                        : 'Discount applied'}
                    </div>
                  )}
                  {voucherValid === false && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                      ✗ Invalid voucher code
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      setAction('idle')
                      setVoucherCode('')
                      setVoucherValid(null)
                      setVoucherInfo(null)
                    }}
                    disabled={processing}
                  >
                    Back
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleRenew}
                    disabled={processing || validatingVoucher}
                  >
                    {processing ? <Spinner label="Processing..." /> : 'Continue to Payment'}
                  </button>
                </div>
              </div>
            )}

            {action === 'billing' && (
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-semibold mb-2">Autopay Settings & ACH Details</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    You'll be redirected to Stripe's secure billing portal where you can:
                  </p>
                  <ul className="text-sm text-gray-600 list-disc list-inside mb-4 space-y-1">
                    <li>Update your payment method (ACH or credit card)</li>
                    <li>Manage autopay settings</li>
                    <li>View payment history and invoices</li>
                    <li>Update billing information</li>
                  </ul>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <button
                    className="btn-secondary"
                    onClick={() => setAction('idle')}
                    disabled={processing}
                  >
                    Back
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleBillingPortal}
                    disabled={processing}
                  >
                    {processing ? <Spinner label="Opening..." /> : 'Open Billing Portal'}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

